<template>
  <div class="container">
    <div class="card-body">
      <h3 class="mb-3">My Notifications</h3>
      <div class="row justify-content-between">
        <div class="my-1 col-md-5 col-sm-6">
          <h6><i class="fa-solid fa-circle-info fa-lg"></i> <b>Note:</b> <i>Notifications older than 30 days are automatically removed.</i></h6>
        </div>
        <div class="my-1 col-md-3 col-sm-1"></div>
        <div class="my-1 col-md-3 col-sm-6"></div>
        <b-button size="sm" @click="markAll()"><i class="fa-solid fa-pen-to-square"></i> Mark All Read</b-button>
      </div>
    </div>
    <div class="row justify-content-between">
      <div class="col-md-4 my-1">
        Showing {{ fromItem }} to {{ toItem }} of
        {{ totalItems }}
        notification<span v-if="totalItems === 0 || totalItems > 1">s</span>
      </div>

      <div class="col-md-3 my-1">
        <b-pagination
          v-if="totalItems > 0"
          :total-rows="totalItems"
          :per-page="perPage"
          v-model="currentPage"
          class="my-0 pagination-holder"
          prev-text="Prev"
          next-text="Next"
          align="fill"
        />
      </div>

      <div class="col-md-3 my-1">
        <b-form-group
          label="Per Page"
          label-align="right"
          label-cols-sm="10"
          label-cols-md="8"
          label-for="perPageSelect"
          class="mb-0"
        >
          <b-form-select
            v-model="perPage"
            id="perPageSelect"
            size="sm"
            :options="pageOptions"
          >
          </b-form-select>
        </b-form-group>
      </div>
    </div>

    <b-table
      show-empty
      stacked="md"
      id="notificationsTable"
      ref="notificationsTable"
      :busy="isBusy"
      :items="notifications"
      :fields="tableFields"
      :current-page="currentPage"
      :per-page="perPage"
      sort-icon-left
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :sort-direction="sortDirection"
    >
      <template #empty>
        <h4 class="text-center">
          You have no unread notifications
        </h4>
      </template>
      <template v-slot:cell(createdAt)="row">
        <DateTime :date="row.item.createdAt" :dateTime="false"></DateTime>
      </template>
      <template v-slot:cell(options)="row">
        <b-btn
          class="mt-1 mr-1"
          alt="View"
          title="View"
          @click="openNotification(row.item)"
        >
          <font-awesome-icon icon="eye"/>
          View
        </b-btn>
        <b-btn
          class="mt-1 mr-1"
          alt="Mark Read"
          title="Mark Read"
          @click="setRead(row.item.id)"
        >
          <font-awesome-icon icon="pencil"/>
          Mark Read
        </b-btn>
        <b-btn
          class="mt-1 mr-1"
          alt="Manage"
          title="Manage"
          @click="openNotificationSettings(row.item)"
        >
          <i class="fa fa-gear"></i>
          Manage Notifications
        </b-btn>
      </template>
      <template #table-busy>
        <VueLoader style="height: 200px" text="Loading Unread Notifications..."/>
      </template>
    </b-table>

    <div class="card-footer">
      <div class="row justify-content-between">
        <div class="col-md-4 my-1">
          Showing {{ fromItem }} to {{ toItem }} of
          {{ totalItems }}
          notification<span v-if="totalItems === 0 || totalItems > 1">s</span>
        </div>

        <div class="col-md-3 my-1">
          <b-pagination
            v-if="totalItems > 0"
            :total-rows="totalItems"
            :per-page="perPage"
            v-model="currentPage"
            class="my-0 pagination-holder"
            prev-text="Prev"
            next-text="Next"
            align="fill"
          />
        </div>

        <div class="col-md-3 my-1">
          <b-form-group
            label="Per Page"
            label-align="right"
            label-cols-sm="10"
            label-cols-md="8"
            label-for="perPageSelect"
            class="mb-0"
          >
            <b-form-select
              v-model="perPage"
              id="perPageSelect"
              size="sm"
              :options="pageOptions"
            >
            </b-form-select>
          </b-form-group>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import VueLoader from "../layout/Loader.vue";
import DateTime from "../common/date-time/DateTime.vue";
import {NOTIFICATION_TYPES} from "../../constants/notification_types";

export default {
  name: "UserNotifications",
  components: {
    VueLoader,
    DateTime
  },
  watch: {
    // Refresh the table when a new notification arrives via Mercure
    lastNotification() {
      if (this.$refs.notificationsTable) {
        this.$refs.notificationsTable.refresh()
      }
    }
  },
  data() {
    return {
      pageOptions: [10, 25, 50, 100, 250, 500],
      isBusy: false,

      tableFields: [
        {
          key: "createdAt",
          label: "Created Date",
          sortable: true,
          sortDirection: "asc",
          tdClass: "text-left",
        },
        {
          key: "type",
          label: "Type",
          sortable: true,
          sortDirection: "asc",
          tdClass: "text-left",
          formatter: val => {
            return NOTIFICATION_TYPES[val]
          }
        },
        {
          key: "title",
          label: "Title",
          sortable: false,
          tdClass: "text-left",
        },
        {
          key: "options",
          label: "Options",
          sortable: false,
          class: "text-right"
        }
      ]
    };
  },

  computed: {
    ...mapGetters({
      isLoading: 'user/notifications/isLoading',
      profileNotifications: 'user/notifications/profileNotifications',
      lastNotification: 'user/notifications/lastNotification',
      notificationPage: 'user/profile/notificationPage',
      notificationItems: 'user/profile/notificationItems',
      notificationTotal: 'user/profile/notificationTotal',
      notificationSortBy: 'user/profile/notificationSortBy',
      notificationSortDirection: 'user/profile/notificationSortDirection',
      notificationSortDesc: 'user/profile/notificationSortDesc',
    }),
    currentPage: {
      get() {
        return this.notificationPage
      },
      set(newValue) {
        this.setNotificationTablePage(newValue)
      }
    },
    totalItems: {
      get() {
        return this.notificationTotal
      },
      set(newValue) {
        this.setNotificationTableTotal(newValue)
      }
    },
    perPage: {
      get() {
        return this.notificationItems
      },
      set(newValue) {
        this.setNotificationTableItems(newValue)
      }
    },
    sortBy: {
      get() {
        return this.notificationSortBy
      },
      set(newValue) {
        this.setNotificationSortBy(newValue)
      }
    },
    sortDirection: {
      get() {
        return this.notificationSortDirection
      },
      set(newValue) {
        this.setNotificationSortDirection(newValue)
      }
    },
    sortDesc: {
      get() {
        return this.notificationSortDesc
      },
      set(newValue) {
        this.setNotificationSortDesc(newValue)
      }
    },

    fromItem() {
      return this.totalItems > 0
        ? (this.currentPage - 1) * this.perPage + 1
        : 0;
    },
    toItem() {
      return this.totalItems < this.currentPage * this.perPage
        ? this.totalItems
        : this.currentPage * this.perPage;
    },
  },
  methods: {
    ...mapActions({
      fetchNotifications: 'user/notifications/get',
      markSingleRead: 'user/notifications/markRead',
      markAllRead: 'user/notifications/markAllRead',
      setNotificationTablePage: 'user/profile/setNotificationTablePage',
      setNotificationTableItems: 'user/profile/setNotificationTableItems',
      setNotificationTableTotal: 'user/profile/setNotificationTableTotal',
      setNotificationSortBy: 'user/profile/setNotificationTableSortBy',
      setNotificationSortDirection: 'user/profile/setNotificationTableSortDirection',
      setNotificationSortDesc: 'user/profile/setNotificationTableSortDesc',
    }),
    notifications(ctx) {
      this.isBusy = true;
      let urlParams = `${this.urlBuilder(ctx)}`;
      const data = {urlParams};
      return this.fetchNotifications(data).then(data => {
        this.totalItems = data["hydra:totalItems"];
        this.currentPage = ctx.currentPage || 1;
        this.sortBy = ctx.sortBy || "";
        this.isBusy = false;
        return data["hydra:member"].map(notification => {
          return {
            ...notification
          }
        });
      });
    },
    openNotification(notification) {
      // Notification is opened
      switch (notification.type) {
        case 'project_resource':
          this.$router.push({name: 'ProjectResources', params: {id: notification.projectUrl}})
          break;
        case 'project_download':
          this.$router.push({name: 'ProjectDownloads', params: {id: notification.projectUrl}})
          break;
        case 'project_observation':
          this.$router.push({name: 'ObservationShow', params: {id: notification.typeIdentifier}})
          break;
        case 'project_forum_post':
        case 'project_forum_reply':
          this.$router.push({
            name: 'ProjectForumDiscussion',
            params: {id: notification.projectUrl, post: notification.typeIdentifier,}
          })
          break;
      }
    },

    openNotificationSettings(notification) {
      this.$router.push({name: 'ProjectNotifications', params: {id: notification.projectUrl}})
    },

    setRead(id) {
      this.markSingleRead(id).then(data => {
        if (data) {
          this.$refs.notificationsTable.refresh()
        }
      })
    },
    markAll() {
      // if (this.totalItems > 0) {
      this.markAllRead().then(val => {
        if (val) {
          this.$refs.notificationsTable.refresh()
        }
      })
      // }
    },
  }
};
</script>

<style scoped lang="scss">

</style>
